/**
 * API Client
 *
 * Handles all HTTP requests with automatic auth token injection,
 * error handling, and token refresh.
 */

import type { ApiResponse } from '@/types';

// Get tokens from localStorage (avoiding circular import with auth store)
function getAccessToken(): string | null {
  try {
    const tokens = localStorage.getItem('sherit_tokens');
    if (tokens) {
      const parsed = JSON.parse(tokens);
      return parsed.accessToken || null;
    }
  } catch {
    // Ignore
  }
  return null;
}

// Base URL - uses proxy in dev, direct in prod
const BASE_URL = import.meta.env.PROD
  ? (import.meta.env.VITE_API_URL || '')
  : '';

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

/**
 * Make an API request
 */
async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  const { skipAuth = false, ...fetchOptions } = options;

  const headers = new Headers(fetchOptions.headers);

  // Add auth token if available
  if (!skipAuth) {
    const token = getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  // Add content type for JSON bodies
  if (fetchOptions.body && typeof fetchOptions.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }

  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
    });

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      if (!response.ok) {
        return { error: `HTTP ${response.status}: ${response.statusText}` };
      }
      return { data: undefined as unknown as T };
    }

    const data = await response.json();

    // Handle 401 - token expired
    if (response.status === 401) {
      // Try to refresh token
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        // Retry original request with new token
        return request<T>(endpoint, options);
      }
      // Refresh failed - clear auth
      localStorage.removeItem('sherit_tokens');
      localStorage.removeItem('sherit_user');
      window.location.href = '/';
      return { error: 'Session expired' };
    }

    // Handle error responses
    if (!response.ok) {
      return {
        error: data.error || data.message || data.msg || `HTTP ${response.status}`,
      };
    }

    return { data };
  } catch (e) {
    console.error('API request failed:', e);
    return {
      error: e instanceof Error ? e.message : 'Network error',
    };
  }
}

/**
 * Try to refresh the access token
 */
async function tryRefreshToken(): Promise<boolean> {
  try {
    const tokens = localStorage.getItem('sherit_tokens');
    if (!tokens) return false;

    const { refreshToken } = JSON.parse(tokens);
    if (!refreshToken) return false;

    const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) return false;

    const data = await response.json();
    if (data.accessToken) {
      const currentTokens = JSON.parse(tokens);
      localStorage.setItem('sherit_tokens', JSON.stringify({
        ...currentTokens,
        accessToken: data.accessToken,
        expiresAt: Date.now() + (data.expiresIn || 3600) * 1000,
      }));
      return true;
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * API client with HTTP method shortcuts
 */
export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),
};

/**
 * Unified Music API - works with any provider
 */
export const musicApi = {
  /**
   * Search for tracks using specified provider
   */
  async search(
    query: string,
    provider: MusicProvider,
    limit = 20
  ): Promise<ApiResponse<MusicSearchResult>> {
    return api.get<MusicSearchResult>(
      `/api/music/search?q=${encodeURIComponent(query)}&provider=${provider}&limit=${limit}`
    );
  },

  /**
   * Get track details by ID
   */
  async getTrack(
    trackId: string,
    provider: MusicProvider
  ): Promise<ApiResponse<MusicTrackResult>> {
    return api.get<MusicTrackResult>(`/api/music/track/${trackId}?provider=${provider}`);
  },

  /**
   * Get available providers for the user
   */
  async getProviders(): Promise<ApiResponse<ProvidersResult>> {
    return api.get<ProvidersResult>('/api/music/providers');
  },

  /**
   * Cross-match a track to another provider (e.g., find Spotify track on Apple Music)
   */
  async crossMatch(
    trackId: string,
    fromProvider: MusicProvider,
    toProvider: MusicProvider
  ): Promise<ApiResponse<CrossMatchResult>> {
    return api.get<CrossMatchResult>(
      `/api/music/cross-match?trackId=${trackId}&from=${fromProvider}&to=${toProvider}`
    );
  },
};

// Legacy Spotify API - for backwards compatibility
export const spotifyApi = {
  async search(query: string, types = 'track', limit = 20): Promise<ApiResponse<SpotifySearchResult>> {
    return api.get<SpotifySearchResult>(
      `/api/music/search?q=${encodeURIComponent(query)}&provider=spotify&type=${types}&limit=${limit}`
    );
  },

  async getTrack(trackId: string): Promise<ApiResponse<SpotifyTrackResult>> {
    return api.get<SpotifyTrackResult>(`/api/music/track/${trackId}?provider=spotify`);
  },
};

// ============================================
// TYPE DEFINITIONS
// ============================================

import type { MusicProvider, SpotifyTrack, AppleMusicTrack, UnifiedTrack } from '@/types';

// Unified search result
interface MusicSearchResult {
  tracks: (SpotifyTrack | AppleMusicTrack | UnifiedTrack)[];
  total: number;
  provider: MusicProvider;
}

// Unified track result
interface MusicTrackResult {
  track: SpotifyTrack | AppleMusicTrack | UnifiedTrack;
  provider: MusicProvider;
}

// Provider info result
interface ProvidersResult {
  providers: Array<{
    id: MusicProvider;
    name: string;
    connected: boolean;
    enabled: boolean;
  }>;
  activeProvider: MusicProvider | null;
}

// Cross-match result
interface CrossMatchResult {
  matched: boolean;
  track?: SpotifyTrack | AppleMusicTrack | UnifiedTrack;
  provider: MusicProvider;
  matchConfidence: number; // 0-1, based on ISRC match vs fuzzy match
}

// Legacy Spotify types (for backward compat)
interface SpotifySearchResult {
  tracks: {
    items: Array<{
      id: string;
      name: string;
      artists: Array<{ id: string; name: string }>;
      album: {
        id: string;
        name: string;
        images: Array<{ url: string; width: number; height: number }>;
      };
      duration_ms: number;
      preview_url: string | null;
    }>;
    total: number;
  };
}

interface SpotifyTrackResult {
  track: {
    id: string;
    name: string;
    artists: Array<{ id: string; name: string }>;
    album: {
      id: string;
      name: string;
      images: Array<{ url: string; width: number; height: number }>;
    };
    duration_ms: number;
    preview_url: string | null;
  };
}
